import type { Customer, CustomerProfile, AuthTokens, LoginResponse, RefreshResponse, RegisterPayload, RegisterResponse, Passport } from '~/types'

export const useCustomerStore = defineStore('customer', () => {
  const { $i18n } = useNuxtApp()
  // Keep the cookie ref out of Pinia state so prod payload hydration can't
  // overwrite `document.cookie` with a stale null. Read/write via composable
  // each time through this writable computed proxy.
  const passportCookie = () =>
    useCookie<Passport | null>(PASSPORT_COOKIE_KEY, {
      ...passportCookieOptions(),
      default: () => null,
    })
  const passport = computed<Passport | null>({
    get: () => passportCookie().value,
    set: (v) => { passportCookie().value = v },
  })

  const customer = ref<Customer | null>(null)

  // Nuxt flushes a `useCookie` write to `document.cookie` asynchronously, so for the
  // rest of the current tick the cookie still holds the PREVIOUS value. Route
  // middleware running in the same tick as login()/logout() would otherwise read a
  // stale session. This in-memory mirror is written synchronously and takes
  // precedence over the cookie whenever it has an opinion.
  //
  // Three states, and the distinction matters in both directions:
  //   undefined — no opinion (fresh page load); the cookie is the source of truth
  //   null      — explicitly logged out
  //   string    — the live access token
  // A plain `sessionToken ?? passport.a` fallback broke logout: clearing the mirror
  // to null fell straight back to the not-yet-written cookie, so `isLoggedIn` stayed
  // true, the route gate let the user through to a page that then 401'd, and the
  // navigation hung on the loading spinner.
  const sessionToken = ref<string | null | undefined>(undefined)

  const accessToken = computed(() =>
    sessionToken.value === undefined ? (passport.value?.a ?? null) : sessionToken.value)
  const isLoggedIn = computed(() => !!accessToken.value)

  function setSession(tokens: AuthTokens, c: Customer): void {
    sessionToken.value = tokens.access_token
    passport.value = { a: tokens.access_token, r: tokens.refresh_token, cid: c.id }
    customer.value = c
  }

  async function loginWithToken(token: string): Promise<void> {
    const res = await $fetch<LoginResponse>(`/api/${$i18n.locale.value}/auth/login-with-token/`, {
      method: 'POST',
      body: { token },
      retry: 0,
    })

    if (!res.success) {
      throw res
    }

    setSession(res.data, res.data.customer)
  }

  async function login(email: string, password: string, b2b?: boolean): Promise<void> {
    const res = await $fetch<LoginResponse>(`/api/${$i18n.locale.value}/auth/login/`, {
      method: 'POST',
      body: { email, password, ...(b2b !== undefined && { b2b }) },
      retry: 0,
    })

    if (!res.success) {
      throw res
    }

    setSession(res.data, res.data.customer)
    useDataLayer().trackLogin('email', res.data.customer.id)
  }

  // De-duplicates concurrent refreshes. Several requests can 401 in the same tick
  // (SSR fan-out, a page firing three calls at once); each used to run its own
  // refresh with the SAME refresh token. Now that the backend makes refresh tokens
  // single-use, the first call wins and the rest fail — and a failed refresh logs
  // the user out, so a working session would be torn down at random. One in-flight
  // promise, shared by every caller.
  let refreshInFlight: Promise<void> | null = null

  function refresh(): Promise<void> {
    if (refreshInFlight) return refreshInFlight
    refreshInFlight = runRefresh().finally(() => { refreshInFlight = null })
    return refreshInFlight
  }

  async function runRefresh(): Promise<void> {
    const r = passport.value?.r

    if (!r) {
      // Must clear the session too. The in-memory `sessionToken` mirror can still
      // hold an access token here, so skipping logout() left `isLoggedIn` true with
      // no way to recover: the gate let the user stay on the page, every call 401'd,
      // and nothing ever sent them back to the login screen.
      logout()
      throw new Error('No refresh token')
    }

    try {
      const res = await $fetch<RefreshResponse>(`/api/${$i18n.locale.value}/auth/refresh/`, {
        method: 'POST',
        body: { refresh_token: r },
      })

      if (!res.success) throw res

      sessionToken.value = res.data.access_token
      passport.value = {
        ...passport.value!,
        a: res.data.access_token,
        r: res.data.refresh_token,
      }
    } catch (err) {
      logout()
      throw err
    }
  }

  /**
   * Clears the session locally and asks the backend to revoke the token pair.
   *
   * Unlike B2C — which has no logout endpoint at all and only forgets the tokens
   * client-side — the B2B API bumps `token_version`, so the old access token stops
   * working immediately. Revocation is best-effort and deliberately not awaited:
   * a failing request must never leave the user apparently still logged in.
   */
  function logout(): void {
    const token = accessToken.value

    sessionToken.value = null
    passport.value = null
    customer.value = null
    profile.value = null

    if (token) {
      void $fetch(`/api/${$i18n.locale.value}/auth/logout/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        retry: 0,
      }).catch(() => {})
    }
  }

  async function register(data: RegisterPayload): Promise<void> {
    const res = await $fetch<RegisterResponse>(`/api/${$i18n.locale.value}/customer/register/`, {
      method: 'POST',
      body: data,
      retry: 0,
    })

    if (!res.success) {
      throw res
    }

    if (res.data?.access_token) {
      passport.value = {
        a: res.data.access_token,
        r: res.data.refresh_token,
        cid: res.data.customer.id,
      }
      customer.value = res.data.customer
      useDataLayer().trackSignUp('email', res.data.customer.id)
    } else {
      // API didn't return tokens — fall back to explicit login
      await login(data.email, data.password)
      useDataLayer().trackSignUp('email', customer.value?.id)
    }
  }

  const profile = ref<CustomerProfile | null>(null)

  async function fetchProfile(): Promise<CustomerProfile | null> {
    const api = useApi()
    // Bounded on the server: this call now runs inside the SSR render (see
    // `plugins/customer-load.ts`), and one hung upstream request must not hold a
    // page open. A timeout surfaces as an ordinary rejection — the plugin logs it
    // and renders without the identity UI rather than failing the request.
    const res = await api.getProfile(import.meta.server ? { timeout: 4000 } : {})
    if (res.success) {
      profile.value = res.data.customer
      syncCustomerFromProfile(res.data.customer)
    }
    return profile.value
  }

  /**
   * Folds a `customer/info` response into `customer`.
   *
   * `customer/info` is what re-establishes the session on every page load — on the
   * server during SSR, and on the client for anything SSR could not supply — so it
   * has to work with **no** prior customer, not only as a refresh of one. Building
   * the object from the profile alone used to be skipped: a browser that held the
   * passport cookie but no cached customer (a new device, cleared storage, the
   * server render) got `is_manager` back from the backend and dropped it on the
   * floor, so the manager toolbar stayed hidden until the next login.
   *
   * The profile is the narrower of the two shapes. Fields it does not carry are
   * kept from the existing customer where there is one, and left undefined on a
   * cold build rather than invented.
   */
  function syncCustomerFromProfile(p: CustomerProfile): void {
    customer.value = {
      ...customer.value,
      id: p.id,
      email: p.email,
      first_name: p.first_name,
      last_name: p.last_name ?? '',
      phone: p.phone ?? '',
      company_name: p.company_name ?? customer.value?.company_name ?? null,
      company_code: p.company_code ?? customer.value?.company_code ?? null,
      vat_code: p.vat_code ?? customer.value?.vat_code ?? null,
      billing_address: p.billing_address ?? customer.value?.billing_address ?? null,
      billing_country: p.billing_country ?? customer.value?.billing_country ?? null,
      billing_country_name: p.billing_country_name ?? customer.value?.billing_country_name ?? null,
      billing_region: p.billing_region ?? customer.value?.billing_region ?? null,
      billing_region_name: p.billing_region_name ?? customer.value?.billing_region_name ?? null,
      billing_post_code: p.billing_post_code ?? customer.value?.billing_post_code ?? null,
      is_manager: p.is_manager ?? customer.value?.is_manager,
      // `auth/login` is the only other source of these, so a cold build (new device,
      // cleared storage, an SSR render) would otherwise drop the assigned contact.
      sales_person_name: p.sales_person_name ?? customer.value?.sales_person_name ?? null,
      sales_person_email: p.sales_person_email ?? customer.value?.sales_person_email ?? null,
      sales_person_phone: p.sales_person_phone ?? customer.value?.sales_person_phone ?? null,
    }
  }

  function setCustomer(c: Customer | null): void {
    customer.value = c
  }

  return { passport, accessToken, customer, profile, isLoggedIn, loginWithToken, login, refresh, logout, register, setCustomer, fetchProfile }
})
